import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Box, Image, Text } from '@chakra-ui/react';
import { Favorite, FavoriteBorder, ShoppingBagOutlined } from '@mui/icons-material';

import { useShopUI } from '../../contexts/ShopUIContext';
import { formatPrice, swatchColor, productPath } from './productUtils';

// Product card (design-system.md §4.2): 2:3 photo, heart, quick-add pill, title, condensed price, swatch.
// `priceSize` is 'small' on the home grid and 'large' on collection grids.
const ProductCard = ({ product, priceSize = 'small' }) => {

    const navigate = useNavigate();
    const { isFavorite, toggleFavorite, openQuickAdd } = useShopUI();
    const fav = isFavorite(product._id);
    const soldOut = product.status === false;

    const stop = (fn) => (e) => { e.preventDefault(); e.stopPropagation(); fn(); };

    return (
        <Box
            as={RouterLink}
            to={productPath(product)}
            state={{ productId: product._id }}
            display='block'
            role='group'
            transition='transform .25s ease-out'
            _hover={{ transform: { lg: 'translateY(-4px)' } }}
        >
            <Box position='relative' overflow='hidden' bg='raano.imageBg' sx={{ aspectRatio: '2 / 3' }} >
                <Image
                    src={product.imageUrl}
                    alt={`${product.name} - Image 1`}
                    loading='lazy'
                    width='100%'
                    height='100%'
                    objectFit='cover'
                    transition='transform .5s'
                    _groupHover={{ transform: { lg: 'scale(1.03)' } }}
                />

                <Box
                    as='button'
                    aria-label={fav ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
                    aria-pressed={fav}
                    position='absolute' top={{ base: '8px', md: '14px' }} right={{ base: '8px', md: '14px' }}
                    width='32px' height='32px' display='flex' alignItems='center' justifyContent='center'
                    color='raano.black' transition='transform .125s' _active={{ transform: 'scale(0.9)' }}
                    onClick={stop(() => toggleFavorite(product._id))}
                >
                    {fav ? <Favorite sx={{ fontSize: 22 }} /> : <FavoriteBorder sx={{ fontSize: 22 }} />}
                </Box>

                {soldOut &&
                    <Text position='absolute' left='8px' bottom='8px' px='8px' py='2px' bg='raano.white' textStyle='condensed' fontSize='12.6px' textTransform='uppercase' >Sold out</Text>}

                {/* Quick add: round on rest, expands to show the label on hover (desktop) */}
                {!soldOut &&
                    <Box
                        as='button'
                        aria-label={`Quick add ${product.name}`}
                        position='absolute' right={{ base: '8px', md: '14px' }} bottom={{ base: '8px', md: '14px' }}
                        height={{ base: '28px', md: '34px' }} minWidth={{ base: '28px', md: '34px' }} px={{ base: '6px', md: '8px' }}
                        display='flex' alignItems='center' justifyContent='center'
                        borderRadius='full' bg='raano.black' color='raano.white' boxShadow='popover'
                        opacity={{ base: 1, lg: 0 }} transform={{ base: 'none', lg: 'translateY(6px)' }}
                        transition='opacity .2s, transform .2s'
                        _groupHover={{ opacity: 1, transform: 'none' }}
                        _focusVisible={{ opacity: 1, transform: 'none', outline: '2px solid', outlineColor: 'raano.black', outlineOffset: '2px' }}
                        sx={{ '&:hover .qa-label, &:focus-visible .qa-label': { maxWidth: '140px', opacity: 1, transform: 'none', ml: '6px' } }}
                        onClick={stop(() => (openQuickAdd ? openQuickAdd(product) : navigate(productPath(product))))}
                    >
                        <ShoppingBagOutlined sx={{ fontSize: 17 }} />
                        <Box className='qa-label' as='span' display={{ base: 'none', lg: 'inline' }} maxWidth={0} opacity={0} overflow='hidden' whiteSpace='nowrap' transform='translateX(1em)'
                            transition='max-width .125s ease-in-out, opacity .125s ease-in-out, transform .125s ease-in-out, margin .125s ease-in-out'
                            textStyle='condensed' fontSize='12.6px' textTransform='uppercase' >Add to cart</Box>
                    </Box>}
            </Box>

            <Text textStyle='cardTitle' color='raano.ink' mt='10px' noOfLines={1} >{product.name}</Text>
            <Text textStyle={priceSize === 'large' ? 'priceLarge' : 'price'} color='raano.ink' mt='6px' >{formatPrice(product.price)}</Text>
            {product.color &&
                <Box mt='8px' display='flex' alignItems='center' gap='6px' title={product.color} >
                    <Box width='17px' height='17px' borderRadius='6px' border='1px solid' borderColor='raano.inkSoft' p='1px' bg='raano.white' >
                        <Box width='100%' height='100%' borderRadius='5px' bg={swatchColor(product.color)} />
                    </Box>
                </Box>}
        </Box>
    );
};

export default ProductCard;
